import {
  Component,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UserAvatar } from '../../../core/components/user-avatar/user-avatar';
import { BehaviorSubject, Subject, takeUntil, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Dock } from '../../../core/components/dock/dock';
import { IconBtn } from '../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { faCompressWide, faRepeat } from '@fortawesome/pro-duotone-svg-icons';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { User, UserStore } from '../../../state/user.repository';
import { UserName } from '../../../shared/components/user/user-name/user-name';
import { MuteStatus } from '../../../core/components/mute-status/mute-status';
import { RaisedHandBadge } from '../../../core/components/raised-hand-badge/raised-hand-badge';
import { TranslatePipe } from '@ngx-translate/core';
import { DoubleTapTracker } from '../../../shared/helpers/double-tap';
import { NativeVideoPipe } from '../../../shared/pipes/native-video.pipe';
import { VideoMountDirective } from '../../../shared/directives/video-mount.directive';

@Component({
  selector: 'andor-video-fullscreen',
  imports: [
    NativeVideoPipe,
    VideoMountDirective,
    AsyncPipe,
    UserAvatar,
    UserName,
    MuteStatus,
    RaisedHandBadge,
    Dock,
    IconBtn,
    FaIconComponent,
    FaDuotoneIconComponent,
    CdkDrag,
    TranslatePipe,
  ],
  templateUrl: './video-fullscreen.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './video-fullscreen.scss',
})
export class VideoFullscreen implements OnInit, OnDestroy {
  protected readonly faRepeat = faRepeat;
  protected readonly faCompressWide = faCompressWide;
  protected controlsVisible = signal(true);
  private _hideTimer?: ReturnType<typeof setTimeout>;
  userId = input.required<string>();
  main = signal<MediaStream | null>(null);
  small = signal<MediaStream | null>(null);
  user$ = new BehaviorSubject<User | undefined>(undefined);
  close = output<void>();
  switched = signal(false);
  streamMain = signal<{
    stream: null | MediaStream;
    class: 'screen-share' | '';
  }>({ stream: null, class: '' });
  streamSmall = signal<{
    stream: null | MediaStream;
    class: 'screen-share' | '';
  }>({ stream: null, class: '' });
  close$ = new Subject();
  constructor(public userStore: UserStore) {}
  ngOnDestroy(): void {
    this.close$.next(null);
    clearTimeout(this._hideTimer);
  }
  ngOnInit(): void {
    this.startHideTimer();
    console.log('VideoFullscreen userID: ', this.userId());
    this.userStore
      .get$(this.userId())
      .pipe(
        takeUntil(this.close$),
        tap((user) => {
          // User left
          if ((!user && this.user$.value) || (this.user$.value && user && !this.hasStreams(user))) {
            this.close.emit();
            return;
          }
          if (!user) return;
          if (!this.user$.value || (this.user$.value && this.hasChanged(user, this.user$.value))) {
            this.handleStreams(user).then();
          }
        })
      )
      .subscribe(this.user$);
  }

  async handleStreams(user: User) {
    let cam: MediaStream | undefined;
    let screen: MediaStream | undefined;
    if (!this.hasStreams(user)) {
      // this.close.emit();
      return;
    }
    if (user.camStream) {
      if (user.camStream && user.camStream.kind === 'video-output') {
        await user.camStream.start();
      }
      cam = user.camStream?.mediaStream;
    }
    if (user.screenCastStream) {
      if (user.screenCastStream && user.screenCastStream.kind === 'video-output') {
        await user.screenCastStream.start();
      }
      screen = user.screenCastStream?.mediaStream;
    }

    if (this.switched()) {
      const switchCam = cam;
      cam = screen;
      screen = switchCam;
    }

    if (screen) {
      this.streamMain.set({ stream: screen, class: 'screen-share' });
      if (cam) {
        this.streamSmall.set({ stream: cam, class: '' });
      } else {
        this.streamSmall.set({ stream: null, class: '' });
      }
    } else {
      if (cam) {
        this.streamMain.set({ stream: cam, class: '' });
        this.streamSmall.set({ stream: null, class: '' });
      } else {
        this.streamMain.set({ stream: null, class: '' });
        this.streamSmall.set({ stream: null, class: '' });
      }
    }
  }

  hasStreams(user: User): boolean {
    return !!(user.camStream || user.screenCastStream);
  }

  swapStreams(user: User) {
    this.switched.update((state) => !state);
    this.handleStreams(user).catch((e) => console.info(e));
  }

  hasChanged(oldUser: User, newUser: User): boolean {
    // Early out if nothing changed
    if (oldUser === newUser) {
      return false;
    }
    // Relevant changes for re-rendering the tile
    if (oldUser.camStream !== newUser.camStream) {
      return true;
    }
    if (oldUser.screenCastStream !== newUser.screenCastStream) {
      return true;
    }
    return false;
  }

  onMouseMove() {
    this.controlsVisible.set(true);
    this.startHideTimer();
  }

  private _doubleTap = new DoubleTapTracker();

  protected onRootClick(event: MouseEvent) {
    // Double tap anywhere closes fullscreen — mirroring the double tap that
    // opened it. The dock and real controls (close/switch buttons) are
    // exempt: their clicks bubble up here (IconBtn doesn't stop
    // propagation), and two quick presses on them must not also close.
    if ((event.target as HTMLElement | null)?.closest('button, .conferencing-controls')) {
      return;
    }
    if (this._doubleTap.isDoubleTap(event)) {
      this.close.emit();
    }
  }

  private startHideTimer() {
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => this.controlsVisible.set(false), 4000);
  }
}
